import {
  createImageElement,
  createVideoElementConfig,
  createAudioElement,
  insertElementIntoNewNonOverlappingTrack,
  updatePlayerConfigWithTracks,
  updateClipData,
  updateElementStyles,
  deleteTrackElement,
  updateElementPosition,
  updateElementDetails
} from './utils';
import { UploadedAsset } from '@/components/molecules/MultiAssetUploader/types';
import { TrackType } from '@/player-config/src/types/Track/Type';
import { saveClipChanges, updateClipWithId } from '@/stores/clip';
import { Clip } from '@/player-config/src/types/clip-types';
import { ElementsTrack } from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { ImageStyleProperties } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import { getClipFPS } from '@/player-config/src/utils/clipUtils';
import { getPlayerConfigElements } from '@/player-config/src/utils/playerConfigGetters';
import { VideoStyleProperties } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { getAudioDurationFromUrl } from '@/libs/getAudioDuration';

export const createImageAssetHandler =
  (clipData: Clip, clipId: string) => (currentFrame: number, asset: UploadedAsset) => {
    const playerConfig = clipData.asset_metadata.player_config;
    if (!playerConfig) {
      return;
    }
    const fps = getClipFPS(clipData);

    const imageElement = createImageElement({ currentFrame, asset, fps });
    const currentTracks = playerConfig?.playerConfig?.tracks ?? [];
    const imageTracks = getPlayerConfigElements(playerConfig);

    const updatedImageTracks = insertElementIntoNewNonOverlappingTrack(imageTracks, imageElement);

    const otherTracks = currentTracks.filter(track => track.type !== TrackType.ELEMENTS);
    const combinedTracks = [...otherTracks, ...updatedImageTracks];

    const updatedPlayerConfig = updatePlayerConfigWithTracks(playerConfig, combinedTracks as ElementsTrack[]);
    const updatedClipData = updateClipData(clipData, updatedPlayerConfig);

    updateClipWithId(clipId, updatedClipData);
    saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
  };

export const createVideoAssetHandler =
  (clipData: Clip, clipId: string) => (currentFrame: number, asset: UploadedAsset) => {
    const playerConfig = clipData.asset_metadata.player_config;
    const videoDuration = asset?.duration;

    if (!playerConfig || !videoDuration) {
      return;
    }
    const fps = getClipFPS(clipData);
    const videoElement = createVideoElementConfig({ currentFrame, asset, fps, duration: videoDuration });

    const currentTracks = playerConfig?.playerConfig?.tracks ?? [];
    const videoTracks = getPlayerConfigElements(playerConfig);

    const updatedVideoTracks = insertElementIntoNewNonOverlappingTrack(videoTracks, videoElement);

    const otherTracks = currentTracks.filter(track => track.type !== TrackType.ELEMENTS);
    const combinedTracks = [...otherTracks, ...updatedVideoTracks];

    const updatedPlayerConfig = updatePlayerConfigWithTracks(playerConfig, combinedTracks as ElementsTrack[]);
    const updatedClipData = updateClipData(clipData, updatedPlayerConfig);

    updateClipWithId(clipId, updatedClipData);
    saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
  };

export const createAudioAssetHandler =
  (clipData: Clip, clipId: string) => async (currentFrame: number, asset: UploadedAsset) => {
    const playerConfig = clipData.asset_metadata.player_config;
    if (!playerConfig) {
      return;
    }
    const fps = getClipFPS(clipData);

    let duration: number | undefined;
    try {
      duration = await getAudioDurationFromUrl(asset.url);
    } catch (error) {
      // If duration fetch fails, continue without it
    }

    const audioElement = createAudioElement({ currentFrame, asset, duration, fps });
    const currentTracks = playerConfig?.playerConfig?.tracks ?? [];
    const audioTracks = getPlayerConfigElements(playerConfig);

    const updatedAudioTracks = insertElementIntoNewNonOverlappingTrack(audioTracks, audioElement);

    const otherTracks = currentTracks.filter(track => track.type !== TrackType.ELEMENTS);
    const combinedTracks = [...otherTracks, ...updatedAudioTracks];

    const updatedPlayerConfig = updatePlayerConfigWithTracks(playerConfig, combinedTracks as ElementsTrack[]);
    const updatedClipData = updateClipData(clipData, updatedPlayerConfig);

    updateClipWithId(clipId, updatedClipData);
    saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
  };

export const updateElementStylesHandler = (
  clipData: Clip,
  clipId: string,
  elementId: string,
  styles: ImageStyleProperties
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const updatedPlayerConfig = updateElementStyles(playerConfig, elementId, styles);
  const updatedClipData = updateClipData(clipData, updatedPlayerConfig);

  updateClipWithId(clipId, updatedClipData);
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
};

export const deleteElementHandler = (clipData: Clip, clipId: string, elementId: string) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const updatedPlayerConfig = deleteTrackElement(playerConfig, elementId);
  const updatedClipData = updateClipData(clipData, updatedPlayerConfig);

  updateClipWithId(clipId, updatedClipData);
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
};

export const changeElementPositionHandler = (
  clipData: Clip,
  clipId: string,
  elementId: string,
  elementStyles: {
    containerStyle: VideoStyleProperties | ImageStyleProperties;
    style: VideoStyleProperties | ImageStyleProperties;
  }
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }
  const updatedPlayerConfig = updateElementPosition(playerConfig, elementId, elementStyles);
  const updatedClipData = updateClipData(clipData, updatedPlayerConfig);

  updateClipWithId(clipId, updatedClipData);
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
};

export const updateElementDetailsHandler = (
  clipData: Clip,
  clipId: string,
  elementId: string,
  details: {
    [key: string]: any;
  },
  frameUpdates?: {
    toFrame?: number;
    fromFrame?: number;
  },
  name?: string
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const updatedPlayerConfig = updateElementDetails(playerConfig, elementId, details, frameUpdates, name);
  const updatedClipData = updateClipData(clipData, updatedPlayerConfig);

  updateClipWithId(clipId, updatedClipData);
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });
};
